import { RiCloseLine, RiSearchLine } from '@remixicon/react';
import IconButton from '../../IconButton';
import {
  LayerId,
  SerializedLayers,
  useEventCallback,
} from '@lidojs/design-core';
import { useEditor } from '@lidojs/design-editor';
import axios from 'axios';
import React, { FC, ChangeEvent, useState, useRef, useEffect } from 'react';
import { isMobile } from 'react-device-detect';
import { useAsync } from 'react-use';
import { debounce } from 'lodash';
import { Buffer } from 'buffer';
import { useTranslation } from 'react-i18next';

import {
  createHeading,
  createSubheading,
  createBodyText,
} from '../../constant/text-effects';

interface Text {
  img: string;
  elements: {
    rootId: LayerId;
    layers: SerializedLayers;
  };
}

const TextContent: FC<{ onClose: () => void }> = ({ onClose }) => {
  const { actions } = useEditor();
  const { t } = useTranslation();
  const [texts, setTexts] = useState<Text[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [keyword, setKeyword] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const qRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const dataRef = useRef(false);

  const loadTextList = useEventCallback(async () => {
    dataRef.current = true;
    setIsLoading(true);

    const params = {
      page: page.toString(),
      limit: '20',
      q: keyword || 'template',
    };

    const response = await axios.get<string[]>(
      `/texts?${new URLSearchParams(params).toString()}`
    );

    const { data } = response;

    const nextTexts = data.map((raw) => {
      const dt = JSON.parse(raw);
      const buffer = Buffer.from(dt.elements, 'base64');
      const decodedString = buffer.toString('utf-8');

      dt.elements = JSON.parse(decodedString);

      return dt;
    });

    if (page > 1) {
      setTexts((prevState) => {
        prevState.push(...nextTexts);
        return prevState;
      });
    } else {
      setTexts([...nextTexts]);
    }

    setIsLoading(false);
    setPage(page + 1);

    if (response.data.length > 0) {
      dataRef.current = false;
    }
  });

  useAsync(async () => {
    await loadTextList();
  }, [loadTextList]);

  useEffect(() => {
    const handleLoadMore = async (e: Event) => {
      const node = e.target as HTMLDivElement;
      if (
        node.scrollHeight - node.scrollTop - 80 <= node.clientHeight &&
        !dataRef.current
      ) {
        await loadTextList();
      }
    };

    scrollRef.current?.addEventListener('scroll', handleLoadMore);
    return () => {
      scrollRef.current?.removeEventListener('scroll', handleLoadMore);
    };
  }, [loadTextList, texts]);

  const handleSearch = async (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
    setKeyword(qRef.current?.value || '');
    setPage(1);
    setTimeout(async () => {
      await loadTextList();
    });
  };

  const headingData = createHeading(t('page_editor.text.add_a_heading'));
  const subheadingData = createSubheading(
    t('page_editor.text.add_a_subheading')
  );
  const bodyTextData = createBodyText(t('page_editor.text.add_a_body_text'));

  const handleAddText = (data: {
    rootId: LayerId;
    layers: SerializedLayers;
  }) => {
    actions.addTextLayer(data);
    if (isMobile) {
      onClose();
    }
  };

  const handleDrag = (
    event: React.DragEvent,
    data: { rootId: LayerId; layers: SerializedLayers }
  ) => {
    const { clientX, clientY } = event;
    actions.startDragNDrop({ layer: 'Text', data }, { x: clientX, y: clientY });
    event.dataTransfer.clearData('text/plain');
    event.dataTransfer.setData(
      'text/plain',
      JSON.stringify({ layer: 'Text', data })
    );
    event.dataTransfer.setDragImage(new Image(), 0, 0);
  };

  return (
    <div
      css={(theme) => ({
        width: '100%',
        height: '100%',
        flexDirection: 'column',
        overflowY: 'auto',
        display: 'flex',
      })}
    >
      <div
        css={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          height: 50,
          borderBottom: `1px solid ${theme.colors.stroke}`,
          padding: '0 20px',
        })}
      >
        <p
          css={(theme) => ({
            lineHeight: '48px',
            fontWeight: 600,
            color: theme.colors.content,
            flexGrow: 1,
          })}
        >
          {t('page_editor.general.text')}
        </p>
        <IconButton onClick={onClose}>
          <RiCloseLine size={20} />
        </IconButton>
      </div>
      <div
        css={(theme) => ({
          flexDirection: 'column',
          overflowY: 'auto',
          display: 'flex',
        })}
      >
        <div
          css={(theme) => ({
            borderRadius: 4,
            border: `1px solid ${theme.colors.stroke}`,
            margin: 16,
          })}
        >
          <div
            css={(theme) => ({
              height: 40,
              borderRadius: 4,
              paddingLeft: 12,
              display: 'flex',
              alignItems: 'center',
              overflow: 'hidden',
            })}
          >
            <div
              css={(theme) => ({
                fontSize: 24,
                marginRight: 8,
                flexShrink: 0,
              })}
            >
              <RiSearchLine size={20} />
            </div>
            <input
              ref={qRef}
              css={(theme) => ({
                width: '100%',
                height: '100%',
                backgroundColor: theme.colors.input,
                paddingLeft: 8,
              })}
              type={'text'}
              onInput={debounce(handleSearch, 400)}
            />
          </div>
        </div>

        {keyword === '' && (
          <div
            css={(theme) => ({
              padding: 16,
              display: 'flex',
              gap: 8,
              flexDirection: 'column',
            })}
          >
            <div
              css={(theme) => ({
                fontSize: 28,
                lineHeight: 1,
                padding: '16px 16px',
                fontWeight: 700,
                background: theme.colors.backgroundItem,
                borderRadius: 4,
                cursor: 'pointer',
                transition: 'box-shadow 0.3s',
                ':hover': {
                  background: '#F9F8F8',
                  boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.10)',
                },
                'html.dark &': {
                  filter: 'brightness(90%)',
                  ':hover': {
                    filter: 'brightness(100%)',
                    boxShadow: 'none',
                    background: theme.colors.backgroundItem,
                  },
                },
                '-webkit-user-drag': 'element',
              })}
              draggable={true}
              onClick={() => handleAddText(headingData)}
              onDragStart={(e) => handleDrag(e, headingData)}
            >
              {t('page_editor.text.add_a_heading')}
            </div>
            <div
              css={(theme) => ({
                fontSize: 18,
                lineHeight: 1,
                padding: '16px',
                fontWeight: 700,
                background: theme.colors.backgroundItem,
                borderRadius: 4,
                cursor: 'pointer',
                transition: 'box-shadow 0.3s',
                ':hover': {
                  background: '#F9F8F8',
                  boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.10)',
                },
                'html.dark &': {
                  filter: 'brightness(90%)',
                  ':hover': {
                    filter: 'brightness(100%)',
                    boxShadow: 'none',
                    background: theme.colors.backgroundItem,
                  },
                },
                '-webkit-user-drag': 'element',
              })}
              draggable={true}
              onClick={() => handleAddText(subheadingData)}
              onDragStart={(e) => handleDrag(e, subheadingData)}
            >
              {t('page_editor.text.add_a_subheading')}
            </div>
            <div
              css={(theme) => ({
                fontSize: 12,
                lineHeight: 1,
                padding: '16px',
                fontWeight: 700,
                background: theme.colors.backgroundItem,
                borderRadius: 4,
                cursor: 'pointer',
                transition: 'box-shadow 0.3s',
                ':hover': {
                  background: '#F9F8F8',
                  boxShadow: '0px 4px 20px 0px rgba(0, 0, 0, 0.10)',
                },
                'html.dark &': {
                  filter: 'brightness(90%)',
                  ':hover': {
                    filter: 'brightness(100%)',
                    boxShadow: 'none',
                    background: theme.colors.backgroundItem,
                  },
                },
                '-webkit-user-drag': 'element',
              })}
              draggable={true}
              onClick={() => handleAddText(bodyTextData)}
              onDragStart={(e) => handleDrag(e, bodyTextData)}
            >
              {t('page_editor.text.add_a_body_text')}
            </div>
          </div>
        )}

        <div
          ref={scrollRef}
          css={(theme) => ({
            flexGrow: 1,
            overflowY: 'auto',
            display: 'grid',
            gridTemplateColumns: 'repeat(3,minmax(0,1fr))',
            gridGap: 8,
            padding: '16px',
            position: 'relative',
          })}
        >
          {texts.map(({ img, elements }, idx) => (
            <div
              key={idx}
              css={(theme) => ({
                cursor: 'pointer',
                position: 'relative',
                paddingBottom: '100%',
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '16px',
                aspectRatio: '1/1',
                borderRadius: 8,
                transition: 'box-shadow 0.3s',
                ':hover': {
                  boxShadow: theme.shadow.hoverItem,
                },
                'html.dark &': {
                  ':hover': {
                    filter: 'brightness(100%)',
                  },
                  backgroundColor: theme.colors.backgroundItem,
                  filter: 'brightness(90%)',
                },
                '-webkit-user-drag': 'element',
              })}
              onClick={() => handleAddText(elements)}
              onDragStart={(e) => handleDrag(e, elements)}
            >
              <img
                css={(theme) => ({
                  height: 'auto',
                  width: '100%',
                  objectFit: 'cover',
                })}
                src={img}
              />
            </div>
          ))}

          {isLoading && (
            <div
              css={(theme) => ({
                textAlign: 'center',
                gridColumnStart: 1,
                gridColumnEnd: 4,
              })}
            >
              Loading...
            </div>
          )}

          {!isLoading && keyword !== '' && !texts.length && (
            <div
              css={(theme) => ({
                gridColumnStart: 1,
                gridColumnEnd: 4,
              })}
            >
              <div
                css={(theme) => ({
                  textAlign: 'center',
                  maxWidth: 240,
                  margin: 'auto',
                })}
              >
                {t('page_editor.error.no_results', { query: keyword })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TextContent;
